import axios from 'axios'

// Base URL for the API
const API_BASE_URL = 

  process.env.REACT_APP_NEW_BASE_V4_URL_MONITORING


// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor to handle MongoDB ObjectId format
apiClient.interceptors.response.use(
  (response) => {
    // Transform response data to handle MongoDB ObjectId format
    if (response.data && typeof response.data === 'object') {
      response.data = transformMongoDBData(response.data)
    }
    return response
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Helper function to transform MongoDB data
const transformMongoDBData = (data) => {
  if (Array.isArray(data)) {
    return data.map(transformMongoDBData)
  } else if (data && typeof data === 'object') {
    // Handle MongoDB ObjectId format: { $oid: "..." }
    if (data.$oid) {
      return data.$oid
    }
    
    // Handle MongoDB Date format: { $date: "..." }
    if (data.$date) {
      return new Date(data.$date).toISOString()
    }
    
    // Handle MongoDB NumberLong format: { $numberLong: "..." }
    if (data.$numberLong) {
      return parseInt(data.$numberLong, 10)
    }
    
    // Recursively transform nested objects
    const transformed = {}
    for (const key in data) {
      transformed[key] = transformMongoDBData(data[key])
    }
    return transformed
  }
  return data
}

// Issue Service Functions

/**
 * Fetch all issues (
 * @param {Object} filters - Optional filters, not used
 */
export const fetchIssues = async (filters = {}) => {
  try {
    const response = await apiClient.get('/issues')
    const issues = response.data.issues || []
    
    //flatten V$ response
    return issues.map(issue => ({
      id: issue.id || issue.issueId || issue._id,
      _id: issue.id || issue.issueId || issue._id,
      issueId: issue.issueNumber || issue.issueId,  
      
      //Basic fields
      title: issue.title,
      description: issue.description,
      category: issue.category,
      priority: issue.priority,
      status: issue.status,
      
      //V3 flat fields 
      issueType: issue.issueType,
      plantId: issue.plantId || issue.source?.plantId,
      plantName: issue.plantName || issue.source?.plantName,
      plantLocation: issue.plantLocation || issue.source?.plantLocation,
      plantCapacity: issue.plantCapacity,
      
      //Assignment 
      assignedTo: issue.assignedTo || issue.assignment?.assignedTo,
      assignedToName: issue.assignedToName || issue.assignment?.assignedToName,
      reportedBy: issue.reportedBy,
      responsibleTeamExternal: issue.responsibleTeamExternal,
      
      //Dates
      createdAt: issue.createdAt || issue.timing?.createdAt,
      updatedAt: issue.updatedAt || issue.timing?.updatedAt,
      expectedClosingDate: issue.expectedClosingDate || issue.timing?.dueDate,
      actualClosingDate: issue.actualClosingDate,
      firstObservedDate: issue.firstObservedDate,
      
      //Tracking
      delayDays: issue.delayDays ?? issue.tracking?.delayDays ?? 0,
      escalationLevel: issue.escalationLevel || issue.tracking?.escalationLevel || 0,
      
      //Details
      affectedEquipment: issue.affectedEquipment,
      severityImpact: issue.severityImpact || issue.severity,
      rootCausePreliminary: issue.rootCausePreliminary,
      actionTaken: issue.actionTaken,
      nextActionFollowUpPlan: issue.nextActionFollowUpPlan,
      customerCommunicationDone: issue.customerCommunicationDone,
      
      //Metadata
      tags: issue.tags || issue.metadata?.tags || [],
      
      // Keep original nested structure for compatibility
      source: issue.source,
      assignment: issue.assignment,
      resolution: issue.resolution,
      timing: issue.timing,
      tracking: issue.tracking,
      metadata: issue.metadata,
    }))
  } catch (error) {
    console.error('Error fetching issues:', error)
    return []
  }
}

/**
 * Fetch a single issue by ID
 * @param {string} issueId - The issue ID
 */
export const fetchIssueById = async (issueId) => {
  try {
    const response = await apiClient.get(`/issues/${issueId}`)
    return response.data.data || response.data
  } catch (error) {
    console.error('Error fetching issue:', error)
    throw error
  }
}

/**
 * Create a new issue
 * @param {Object} issueData - Issue data from the form
 */
export const createIssue = async (issueData) => {
  try {
    const response = await apiClient.post('/issues', issueData)
    const result = response.data.data || response.data
    // Ensure we have an ID for the frontend
    if (result && !result.id) {
      result.id = result.issueId || result._id
    }
    return result
  } catch (error) {
    console.error('Error creating issue:', error)
    throw new Error(error.response?.data?.message || 'Failed to create issue')
  }
}

/**
 * Update an existing issue
 * @param {string} issueId - The issue ID
 * @param {Object} issueData - Updated issue data
 */
export const updateIssue = async (issueId, issueData) => {
  try {
    const response = await apiClient.put(`/issues/${issueId}`, issueData)
    const result = response.data.data || response.data
    // Ensure we have an ID for the frontend
    if (result && !result.id) {
      result.id = result.issueId || result._id
    }
    return result
  } catch (error) {
    console.error('Error updating issue:', error)
    throw new Error(error.response?.data?.message || 'Failed to update issue')
  }
}

/**
 * Delete an issue
 * @param {string} issueId 
 */
export const deleteIssue = async (issueId) => {
  try {
    // Use the MongoDB ObjectID for deletion
    await apiClient.delete(`/issues/${issueId}`)
    return { success: true }
  } catch (error) {
    console.error('Error deleting issue:', error)
    throw new Error(error.response?.data?.message || 'Failed to delete issue')
  }
}

/**
 * Add a comment to an issue
 * @param {string} issueId - The issue ID
 * @param {Object} commentData - Comment data
 */
export const addComment = async (issueId, commentData) => {
  try {
    const response = await apiClient.post(`/issues/${issueId}/comments`, commentData)
    return response.data.data || response.data
  } catch (error) {
    console.error('Error adding comment:', error)
    throw error
  }
}

/**
 * Get comments for an issue
 * @param {string} issueId - The issue ID
 */
export const getIssueComments = async (issueId) => {
  try {
    const response = await apiClient.get(`/issues/${issueId}/comments`)
    return response.data.data || response.data || []
  } catch (error) {
    console.error('Error fetching comments:', error)
    return []
  }
}

/**
 * Update issue status
 * @param {string} issueId - The issue ID
 * @param {string} status - New status
 * @param {string} notes - Optional notes
 */
export const updateIssueStatus = async (issueId, status, notes = '') => {
  try {
    const response = await apiClient.put(`/issues/${issueId}/status`, { newStatus: status, resolutionNotes: notes })
    
    return response.data.data || response.data
  } catch (error) {
    console.error('Error updating issue status:', error)
    throw error
  }
}

/**
 * Assign issue to a user
 * @param {string} issueId - The issue ID
 * @param {string} assignedTo - User ID/name to assign
 * @param {string} assignedToName - User name for display
 */
export const assignIssue = async (issueId, assignedTo, assignedToName = '') => {
  try {
    const response = await apiClient.patch(`/issues/${issueId}/assign`, { assignedTo })
    return response.data.data || response.data
  } catch (error) {
    console.error('Error assigning issue:', error)
    throw error
  }
}

/**
 * Update issue priority
 * @param {string} issueId - The issue ID
 * @param {string} priority - New priority
 */
export const updateIssuePriority = async (issueId, priority) => {
  try {
    const response = await apiClient.put(`/issues/${issueId}`, { priority })
    return response.data.data || response.data
  } catch (error) {
    console.error('Error updating issue priority:', error)
    throw error
  }
}

/**
 * Escalate an issue
 * @param {string} issueId - The issue ID
 * @param {string} escalationLevel - New escalation level
 */
export const escalateIssue = async (issueId, escalationLevel) => {
  try {
    const response = await apiClient.put(`/issues/${issueId}`, { escalationLevel })
    
    return response.data.data || response.data
  } catch (error) {
    console.error('Error escalating issue:', error)
    throw error
  }
}


/**
 * Fetch plant list for dropdowns
 */
export const fetchPlantListNew = async () => {
  try {
    const response = await apiClient.get('/plants')

    const data = response.data.data || response.data
    const plants = Array.isArray(data) ? data : (data?.plants || [])
    return plants.map((plant) => ({
      ...plant,
      _id: plant.id,
      id: plant.id,
      plantName: plant.plantName || plant.name,
    }))
  } catch (error) {
    console.error('Error fetching plants:', error)
    return getMockPlants()
  }
}

/**
 * Fetch user list for assignment dropdowns
 * Note: This should come from core microservice in V4
 */
export const fetchUserList = async () => {
  try {
    // TODO: Update to use core microservice endpoint when available
    // For now, return mock users since endpoint doesn't exist yet
    console.warn('User endpoint not yet available in V4, using mock data')
    return getMockUsers()
  } catch (error) {
    console.error('Error fetching users:', error)
    return getMockUsers()
  }
}

// Upload attachment
/**
 * Upload attachment for an issue
 * @param {string} issueId - The issue ID
 * @param {File} file - File to upload
 */
export const uploadAttachment = async (issueId, file) => {
  try {
    console.log('Uploading attachment for issue:', issueId)
    console.log('File:', file)
    
    const formData = new FormData()
    formData.append('file', file)
    formData.append('issueId', issueId)

    const response = await apiClient.post(`/issues/${issueId}/files/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    
    console.log('Upload response:', response.data)
    return response.data.data || response.data
  } catch (error) {
    console.error('Error uploading attachment:', error)
    console.error('Error details:', error.response?.data)
    throw error
  }
}

/**
 * Fetch attachments for an issue
 * @param {string} issueId - The issue ID
 */
export const fetchIssueAttachment = async (issueId) => {
  try {
    const response = await apiClient.get(`/issues/${issueId}/files`)
    return response.data.data || response.data || []
  } catch (error) {
    console.error('Error fetching issue attachments:', error)
    return []
  }
}

/**
 * Delete an attachment
 * @param {string} issueId - The issue ID
 * @param {string} fileId - The file ID
 */
export const deleteAttachment = async (issueId, fileId) => {
  try {
    await apiClient.delete(`/issues/${issueId}/files/${fileId}`)
    return { success: true }
  } catch (error) {
    console.error('Error deleting attachment:', error)
    throw error
  }
}

/**
 * Fetch frequent tags
 */
export const fetchFrequentTags = async (limit = 4) => {
  try {
    // Send limit as a string to match controller expectation
    const response = await apiClient.get('/issues/frequent-tags', {
      params: { limit: String(limit) }
    })
    // V4 API wraps response in { status, message, data, timestamp }
    return response.data.data || response.data || []
  } catch (error) {
    // If server returns 400 (bad request), retry without params as a fallback
    console.error('Error fetching frequent tags:', error)
    if (error?.response?.status === 400) {
      try {
        const fallback = await apiClient.get('/issues/frequent-tags')
        return fallback.data.data || fallback.data || []
      } catch (err) {
        console.error('Fallback fetch for frequent tags also failed:', err)
        return []
      }
    }

    return []
  }
}

// Mock data for development

const getMockIssues = () => [
  {
    id: '67a1b2c3d4e5f67890123456',
    title: 'Inverter 1 Performance Drop',
    description: 'Inverter 1 showing 15% drop in performance compared to expected output.',
    plantId: '67a1b2c3d4e5f67890123457',
    plantName: 'Solar Plant Alpha',
    category: 'Equipment Failure',
    priority: 'High',
    status: 'Open',
    assignedTo: 'user-1',
    createdAt: '2024-06-20T10:00:00Z',
    expectedClosingDate: '2024-06-25T10:00:00Z',
    tags: ['inverter', 'performance', 'urgent'],
    affectedEquipment: 'Inverter 1',
    severityImpact: 'High',
    createdBy: 'System Admin',
    comments: [
      {
        id: 1,
        text: 'Issue reported by monitoring system',
        author: 'System',
        createdAt: '2024-06-20T10:00:00Z',
        type: 'comment',
      },
    ],
  },
  {
    id: '2',
    issueId: 'ISS-002',
    title: 'Energy Meter Communication Error',
    description:
      'Energy meter 2 not responding to data requests. Communication timeout errors logged.',
    plantId: 'plant-1',
    plantName: 'Solar Plant Alpha',
    category: 'Sensor Issues',
    priority: 'Medium',
    status: 'In Progress',
    assignedTo: 'user-2',
    assignedToName: 'Jane Smith',
    createdAt: '2024-06-19T14:30:00Z',
    expectedClosingDate: '2024-06-24T14:30:00Z',
    tags: ['energy-meter', 'communication'],
    affectedEquipment: 'Energy Meter 2',
    severityImpact: 'Medium',
    createdBy: 'Tech Team',
    comments: [],
  },
  {
    id: '3',
    issueId: 'ISS-003',
    title: 'Scheduled Maintenance Required',
    description: 'Quarterly maintenance due for all inverters in Plant Beta.',
    plantId: 'plant-2',
    plantName: 'Solar Plant Beta',
    category: 'Maintenance Required',
    priority: 'Low',
    status: 'Open',
    assignedTo: null,
    assignedToName: null,
    createdAt: '2024-06-18T09:00:00Z',
    expectedClosingDate: '2024-06-30T09:00:00Z',
    tags: ['maintenance', 'scheduled'],
    affectedEquipment: 'All Inverters',
    severityImpact: 'Low',
    createdBy: 'Maintenance Team',
    comments: [],
  },
  {
    id: '4',
    issueId: 'ISS-004',
    title: 'Grid Connection Instability',
    description:
      'Frequent grid disconnections observed. Need to check grid parameters and connection stability.',
    plantId: 'plant-1',
    plantName: 'Solar Plant Alpha',
    category: 'Grid Issues',
    priority: 'High',
    status: 'Resolved',
    assignedTo: 'user-1',
    assignedToName: 'John Doe',
    createdAt: '2024-06-15T11:15:00Z',
    expectedClosingDate: '2024-06-20T11:15:00Z',
    resolvedAt: '2024-06-19T16:30:00Z',
    tags: ['grid', 'stability', 'connection'],
    affectedEquipment: 'Grid Connection',
    severityImpact: 'High',
    createdBy: 'Operations Team',
    comments: [
      {
        id: 1,
        text: 'Investigation started',
        author: 'John Doe',
        createdAt: '2024-06-15T12:00:00Z',
        type: 'comment',
      },
      {
        id: 2,
        text: 'Found loose connection in main panel',
        author: 'John Doe',
        createdAt: '2024-06-19T15:00:00Z',
        type: 'comment',
      },
      {
        id: 3,
        text: 'Connection repaired and tested',
        author: 'John Doe',
        createdAt: '2024-06-19T16:30:00Z',
        type: 'comment',
      },
    ],
  },
]

const getMockPlants = () => [
  {
    id: '67a1b2c3d4e5f67890123457',
    plantName: 'Solar Plant Alpha',
    projectCode: 'SPA-001',
    plantLocation: 'Location A',
  },
  {
    id: 'plant-2',
    plantName: 'Solar Plant Beta',
    projectCode: 'SPB-002',
    plantLocation: 'Location B',
  },
  {
    id: 'plant-3',
    plantName: 'Solar Plant Gamma',
    projectCode: 'SPG-003',
    plantLocation: 'Location C',
  },
]

const getMockUsers = () => [
  {
    userId: 'user-1',
    name: 'John Doe',
    email: 'john.doe@company.com',
    role: 'Senior Technician',
  },
  {
    id: 'user-2',
    name: 'Jane Smith',
    email: 'jane.smith@company.com',
    role: 'Operations Manager',
  },
  {
    id: 'user-3',
    name: 'Mike Johnson',
    email: 'mike.johnson@company.com',
    role: 'Maintenance Specialist',
  },
  {
    id: 'user-4',
    name: 'Sarah Wilson',
    email: 'sarah.wilson@company.com',
    role: 'System Administrator',
  },
]

export default {
  fetchIssues,
  fetchIssueById,
  createIssue,
  updateIssue,
  deleteIssue,
  updateIssueStatus,
  updateIssuePriority,
  assignIssue,
  //fetchPlantList
  escalateIssue,  
  addComment,
  getIssueComments,  
  uploadAttachment,
  fetchIssueAttachment,
  deleteAttachment,
  fetchPlantListNew,
  fetchUserList,
  fetchFrequentTags,
}

